import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Download, Receipt, ShoppingBag } from "lucide-react";
import api, { API_BASE } from "../lib/api";

/**
 * Post-Cashfree redirect landing page. Shows a "Thank you" confirmation,
 * the full order summary (line items, amount, invoice) and clear next-steps.
 */
export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const orderId = params.get("order_id");
  const [payment, setPayment] = useState(null);
  const [linkedOrder, setLinkedOrder] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [state, setState] = useState("loading"); // 'loading' | 'paid' | 'pending' | 'error'
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) { setState("error"); setError("Missing order_id in URL"); return; }
    let attempts = 0;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/${orderId}`);
        setPayment(data);
        // Enrich with linked DaaS order details if applicable
        if (data.linked_order_id && data.linked_type?.startsWith("daas") && !linkedOrder) {
          api.get(`/daas/orders/${data.linked_order_id}`).then((r) => setLinkedOrder(r.data)).catch(() => {});
        }
        if (data.status === "PAID") {
          setState("paid");
          // Try to fetch matching invoice (may take a moment to generate)
          api.get("/invoices").then((r) => {
            const inv = (r.data || []).find((i) => i.order_id === orderId);
            if (inv) setInvoice(inv);
          }).catch(() => {});
          return;
        }
        if (attempts < 8) setTimeout(poll, 2000);
        else setState("pending");
      } catch (e) {
        if (attempts < 8) setTimeout(poll, 2000);
        else { setState("error"); setError(e?.response?.data?.detail || "Could not load your order"); }
      }
    };
    poll();
    return () => { stopped = true; };
  }, [orderId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-[#F8FBF6] py-10 px-4">
      <div className="max-w-3xl mx-auto" data-testid="checkout-return">
        {state === "loading" && <LoadingCard orderId={orderId} />}
        {state === "paid" && (
          <PaidCard
            payment={payment}
            linkedOrder={linkedOrder}
            invoice={invoice}
            orderId={orderId}
            onGoto={(href) => nav(href)}
          />
        )}
        {state === "pending" && <PendingCard orderId={orderId} />}
        {state === "error" && <ErrorCard error={error} />}
      </div>
    </div>
  );
}

const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");
const fmtDate = (iso) => (iso || "").slice(0, 19).replace("T", " ");

// -------------------------------------------------------------------------
function LoadingCard({ orderId }) {
  return (
    <div className="glass rounded-lg p-10 text-center">
      <Loader2 size={44} className="animate-spin text-navy mx-auto mb-4" />
      <div className="font-heading text-xl text-ink">Confirming your payment…</div>
      <div className="text-sm text-ink-muted mt-2">
        We&apos;re checking with Cashfree. This usually takes a couple of seconds.
      </div>
      {orderId && <div className="text-xs font-mono text-ink-muted mt-4">Order: {orderId}</div>}
    </div>
  );
}

function PaidCard({ payment, linkedOrder, invoice, orderId, onGoto }) {
  const isDaas = payment?.linked_type?.startsWith("daas");
  const isStartupDaas = payment?.linked_type === "daas_startup";
  const isInvestorDaas = payment?.linked_type === "daas_investor";
  const isSubscription = !isDaas;
  const continueHref = isStartupDaas ? "/startup/diligence"
    : isInvestorDaas ? "/investor/diligence-orders"
    : "/billing";

  return (
    <>
      {/* Hero success */}
      <div className="glass rounded-lg p-8 mb-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.06] bg-[radial-gradient(circle_at_top,_#0F3D2E,_transparent_65%)]" />
        <div className="relative">
          <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-emerald-100 mb-4">
            <CheckCircle2 size={44} className="text-emerald-600" />
          </div>
          <div className="font-display text-3xl text-ink mb-1">Thank you for your payment</div>
          <div className="text-sm text-ink-muted">Your order has been confirmed and our team is on it.</div>
          {payment?.final_amount_inr != null && (
            <div className="text-4xl font-display text-navy mt-6">{inr(payment.final_amount_inr)}</div>
          )}
          <div className="text-xs font-mono text-ink-muted mt-1">
            Order {orderId} · {fmtDate(payment?.paid_at || payment?.created_at)}
          </div>
        </div>
      </div>

      {/* Line items */}
      <div className="glass rounded-lg p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <ShoppingBag size={16} className="text-navy" />
          <div className="font-heading text-ink">Order Details</div>
        </div>

        {linkedOrder ? (
          <div className="space-y-3">
            {(linkedOrder.module_ids?.length || 0) > 0 && linkedOrder.module_ids.map((m, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-navy/5 last:border-0">
                <div className="text-sm text-ink">{humanise(m)}</div>
                <div className="text-xs font-mono text-ink-muted">Module</div>
              </div>
            ))}
            {linkedOrder.package_id && (
              <div className="flex items-center justify-between py-2 border-b border-navy/5">
                <div className="text-sm text-ink">{humanise(linkedOrder.package_id)}</div>
                <div className="text-xs font-mono text-ink-muted">Package</div>
              </div>
            )}
            <div className="flex items-center justify-between pt-3 border-t-2 border-navy/10">
              <div className="text-sm font-medium text-ink">Total paid</div>
              <div className="font-heading text-lg text-ink">{inr(linkedOrder.total_inr)}</div>
            </div>
            {(linkedOrder.savings_inr || 0) > 0 && (
              <div className="flex items-center justify-between text-xs text-emerald-700 font-mono">
                <span>Package savings</span>
                <span>− {inr(linkedOrder.savings_inr)}</span>
              </div>
            )}
            <div className="mt-4">
              <div className="overline mb-2">Order Timeline</div>
              <div className="space-y-1.5">
                {(linkedOrder.history || []).slice(-4).map((h, i) => (
                  <div key={i} className="text-xs text-ink-soft flex items-start gap-2">
                    <span className="font-mono text-ink-muted min-w-[130px]">{fmtDate(h.at)}</span>
                    <span>{h.action}{h.note ? ` — ${h.note}` : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : isSubscription ? (
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between py-2 border-b border-navy/5">
              <div className="text-ink">{payment?.plan_id ? humanise(payment.plan_id) : "Membership plan"}</div>
              <div className="font-mono text-ink-muted text-xs">Subscription</div>
            </div>
            {(payment?.coupon_code) && (
              <div className="flex items-center justify-between py-1 text-xs text-emerald-700 font-mono">
                <span>Coupon: {payment.coupon_code}</span>
                <span>− {inr(payment?.coupon_discount_inr)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-3 border-t-2 border-navy/10">
              <div className="font-medium text-ink">Total paid</div>
              <div className="font-heading text-lg text-ink">{inr(payment?.final_amount_inr)}</div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-ink-muted">Loading order items…</div>
        )}
      </div>

      {/* Receipt / next actions */}
      <div className="glass rounded-lg p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Receipt size={16} className="text-navy" />
          <div className="font-heading text-ink">Receipt & Documents</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {invoice ? (
            <a
              href={`${API_BASE}/invoices/${invoice.invoice_id}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="border border-navy/15 hover:border-gold rounded-md p-4 flex items-center gap-3 transition-colors"
              data-testid="checkout-invoice-link"
            >
              <Download size={18} className="text-navy" />
              <div className="min-w-0">
                <div className="text-sm text-ink font-medium">Download Invoice PDF</div>
                <div className="text-xs text-ink-muted font-mono truncate">{invoice.invoice_number}</div>
              </div>
            </a>
          ) : (
            <div className="border border-dashed border-navy/15 rounded-md p-4 flex items-center gap-3 text-ink-muted">
              <Loader2 size={16} className="animate-spin" />
              <div className="text-xs">GST-compliant invoice is being generated…</div>
            </div>
          )}
          <Link
            to="/billing"
            className="border border-navy/15 hover:border-gold rounded-md p-4 flex items-center gap-3 transition-colors"
            data-testid="checkout-billing-link"
          >
            <Receipt size={18} className="text-navy" />
            <div>
              <div className="text-sm text-ink font-medium">All invoices & billing</div>
              <div className="text-xs text-ink-muted">See past payments</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Primary CTA */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <button
          onClick={() => onGoto(continueHref)}
          className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-navy text-white rounded-md hover:bg-[#256E58] transition-colors font-heading"
          data-testid="checkout-return-continue"
        >
          {isDaas ? "View my orders" : "Explore VenturaZ"} <ArrowRight size={16} />
        </button>
        <Link
          to="/"
          className="text-center text-sm text-ink-muted hover:text-ink px-4 py-3"
        >
          Back to home
        </Link>
      </div>
    </>
  );
}

function PendingCard({ orderId }) {
  return (
    <div className="glass rounded-lg p-10 text-center">
      <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 mb-4">
        <Loader2 size={30} className="text-amber-600 animate-spin" />
      </div>
      <div className="font-display text-2xl text-ink mb-2">Payment is being confirmed</div>
      <div className="text-sm text-ink-muted max-w-lg mx-auto">
        Cashfree hasn&apos;t confirmed your payment yet. This can take a few minutes. If the amount was deducted, it will settle shortly and you&apos;ll receive an email with your invoice.
      </div>
      <div className="text-xs font-mono text-ink-muted mt-4">Order: {orderId}</div>
      <div className="mt-6 flex justify-center gap-3">
        <button onClick={() => window.location.reload()} className="px-4 py-2 border border-navy/20 rounded-md text-sm hover:bg-navy/5">
          Refresh
        </button>
        <Link to="/billing" className="px-4 py-2 bg-navy text-white rounded-md text-sm hover:bg-[#256E58]">
          View my orders
        </Link>
      </div>
    </div>
  );
}

function ErrorCard({ error }) {
  return (
    <div className="glass rounded-lg p-10 text-center">
      <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-rose-100 mb-4">
        <XCircle size={30} className="text-rose-600" />
      </div>
      <div className="font-display text-2xl text-ink mb-2">Something went wrong</div>
      <div className="text-sm text-ink-muted">{error}</div>
      <div className="mt-6">
        <Link to="/pricing" className="px-4 py-2 bg-navy text-white rounded-md text-sm hover:bg-[#256E58]">
          Back to pricing
        </Link>
      </div>
    </div>
  );
}

function humanise(id) {
  return String(id || "")
    .replace(/^(startup_|investor_|dao_|daas_)/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
